import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import {
  availableBy,
  daysBetween,
  netPerExtraHour,
  simulate,
} from '@actual-app/core/shared/mtxbudget';
import type { Project } from '@actual-app/core/shared/mtxbudget';

import { useNavigate } from '#hooks/useNavigate';

import { NotConfigured } from './QuinzainePage';
import {
  hoursLabel,
  Line,
  monthName,
  MtxPage,
  Section,
  textDate,
  useMoney,
} from './ui';
import { useMtxbudget } from './useMtxbudget';

export function DettesProjetsPage() {
  const { t } = useTranslation();
  const money = useMoney();
  const { config, saveConfig, debts, monthlyMargin, today, isLoading, plan } =
    useMtxbudget();
  const navigate = useNavigate();
  const [savingName, setSavingName] = useState('');
  const [savingRoom, setSavingRoom] = useState('');

  if (!config) {
    return (
      <MtxPage title={t('Dettes et projets')}>
        {!isLoading && <NotConfigured />}
      </MtxPage>
    );
  }

  const outcome = simulate({
    startMonth: today.slice(0, 7),
    debts,
    projects: config.projects,
    monthlyMargin,
    savings: config.savings,
    emergencyFundTarget: config.emergencyFundTarget,
  });
  const ordered = outcome.debts.flatMap(result => {
    const debt = debts.find(d => d.id === result.id);
    return debt ? [{ debt, result }] : [];
  });
  const totalDebt = debts.reduce((sum, d) => sum + d.balance, 0);
  function deadlineText(project: Project): string {
    if (!project.wished) {
      return t('sans échéance');
    }
    switch (project.deadline) {
      case 'period':
        return t('entre le {{from}} et le {{to}}', {
          from: textDate(project.wishedFrom ?? project.wished),
          to: textDate(project.wished),
        });
      case 'month':
        return t('en {{month}}', {
          month: monthName(project.wished.slice(0, 7)),
        });
      case 'end-of-week':
        return t('avant la fin de la semaine ({{date}})', {
          date: textDate(project.wished),
        });
      case 'end-of-month':
        return t('avant la fin du mois ({{date}})', {
          date: textDate(project.wished),
        });
      case 'end-of-year':
        return t("avant la fin de l'année");
      default:
        return t('pour le {{date}}', { date: textDate(project.wished) });
    }
  }

  // The last date the fortnight plan covers; beyond it, the monthly
  // simulation takes over.
  const planEnd = plan.length > 0 ? plan[plan.length - 1].nextPayday : today;

  // Weekly overtime hours that would close a gap by the wished date.
  function extraHours(missing: number, wishedDate: string | undefined) {
    if (!config || !wishedDate) {
      return 0;
    }
    const weeks = Math.max(1, daysBetween(today, wishedDate) / 7);
    const perHour = netPerExtraHour(config.pay, config.pay.regularHoursPerWeek);
    return perHour > 0 ? Math.ceil((missing / weeks / perHour) * 2) / 2 : 0;
  }

  const parsedRoom = Math.round(
    Number(savingRoom.replace(/\s/g, '').replace(',', '.')) * 100,
  );
  const canAddSaving =
    savingName.trim() !== '' && Number.isFinite(parsedRoom) && parsedRoom > 0;

  function addSaving() {
    if (!config || !canAddSaving) {
      return;
    }
    saveConfig({
      ...config,
      savings: [
        ...config.savings,
        {
          id: `s-${Date.now().toString(36)}`,
          name: savingName.trim(),
          room: parsedRoom,
        },
      ],
    });
    setSavingName('');
    setSavingRoom('');
  }

  function removeSaving(id: string) {
    if (config) {
      saveConfig({
        ...config,
        savings: config.savings.filter(saving => saving.id !== id),
      });
    }
  }

  function setEmergencyFund(value: string) {
    const target = Math.round(
      Number(value.replace(/\s/g, '').replace(',', '.')) * 100,
    );
    if (config && Number.isFinite(target) && target >= 0) {
      saveConfig({ ...config, emergencyFundTarget: target });
    }
  }

  return (
    <MtxPage title={t('Dettes et projets')}>
      <View style={{ gap: 12, paddingBottom: 20 }}>
        <Section style={{ padding: 16, gap: 4 }}>
          <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
            {t('Marge mensuelle à vos heures garanties')}
          </Text>
          <Text
            style={{
              fontSize: 26,
              fontWeight: 600,
              color:
                monthlyMargin >= 0 ? theme.noticeTextLight : theme.errorText,
            }}
          >
            {money(monthlyMargin)}
          </Text>
          <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
            {t(
              "Elle va au fonds d'urgence, à la dette la plus chère, à l'épargne enregistrée, puis aux projets.",
            )}
          </Text>
        </Section>

        <Section
          title={t('Ordre de remboursement')}
          aside={t('{{amount}} au total', { amount: money(totalDebt) })}
        >
          {ordered.map(({ debt, result }, index) => (
            <Line
              key={debt.id}
              last={index === ordered.length - 1}
              lead={String(result.order)}
              title={debt.name}
              detail={
                debt.canPayMore === false
                  ? t('{{rate}} % · versement fixe', {
                      rate: debt.rate.toLocaleString('fr-CA'),
                    })
                  : t('{{rate}} % · minimum {{payment}} par mois', {
                      rate: debt.rate.toLocaleString('fr-CA'),
                      payment: money(debt.monthlyPayment),
                    })
              }
              value={money(debt.balance)}
              valueDetail={
                result.paidOff
                  ? t('soldée en {{month}}', {
                      month: monthName(result.paidOff),
                    })
                  : t('au-delà de 20 ans')
              }
            />
          ))}
          {ordered.length === 0 && (
            <Text style={{ padding: 16, color: theme.pageTextSubdued }}>
              {t('Aucune dette renseignée.')}
            </Text>
          )}
        </Section>

        <Section title={t("Fonds d'urgence")}>
          <Line
            last
            title={t('Montant à garder de côté')}
            detail={
              config.emergencyFundTarget === 0
                ? t('Aucun : toute la marge va aux dettes.')
                : outcome.emergencyFund
                  ? t(
                      'Constitué en {{month}}, avant tout remboursement accéléré.',
                      {
                        month: monthName(outcome.emergencyFund),
                      },
                    )
                  : t('Hors de portée avec la marge actuelle.')
            }
            value={
              <Input
                aria-label={t("Montant du fonds d'urgence")}
                inputMode="decimal"
                defaultValue={String(config.emergencyFundTarget / 100)}
                onUpdate={setEmergencyFund}
                style={{ width: 96, minHeight: 44, textAlign: 'right' }}
              />
            }
          />
        </Section>

        <Section
          title={t('Épargne enregistrée')}
          aside={t('après les dettes, avant les projets')}
        >
          {config.savings.map(saving => {
            const filled = outcome.savings.find(
              s => s.id === saving.id,
            )?.filled;
            return (
              <Line
                key={saving.id}
                title={saving.name}
                detail={t('{{amount}} de droits de cotisation', {
                  amount: money(saving.room),
                })}
                value={filled ? monthName(filled) : t('Hors de portée')}
                valueDetail={
                  <Button
                    variant="bare"
                    aria-label={t('Retirer {{name}}', { name: saving.name })}
                    onPress={() => removeSaving(saving.id)}
                    style={{ minHeight: 44 }}
                  >
                    <Trans>Retirer</Trans>
                  </Button>
                }
              />
            );
          })}
          <View style={{ padding: 12, gap: 8 }}>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <Input
                aria-label={t('Nom du régime')}
                placeholder={t('CELI, REER, CELIAPP, REEE')}
                value={savingName}
                onChangeValue={setSavingName}
                style={{ flex: 1, minWidth: 0, minHeight: 44 }}
              />
              <Input
                aria-label={t('Droits de cotisation')}
                placeholder={t('Droits de cotisation')}
                inputMode="decimal"
                value={savingRoom}
                onChangeValue={setSavingRoom}
                style={{ flex: 1, minWidth: 0, minHeight: 44 }}
              />
            </View>
            <Button
              isDisabled={!canAddSaving}
              onPress={addSaving}
              style={{ minHeight: 44 }}
            >
              <Trans>Ajouter le régime</Trans>
            </Button>
          </View>
        </Section>

        <Section title={t("Besoins d'investissement")}>
          {config.projects.map(project => {
            const result = outcome.projects.find(p => p.id === project.id);
            // A need due within the fortnight plan is checked against what
            // the pays really leave by that date, earlier needs served first.
            const near =
              project.wished !== undefined && project.wished <= planEnd;
            const earlier = config.projects
              .filter(
                other =>
                  other.priority < project.priority &&
                  other.wished !== undefined &&
                  other.wished <= planEnd,
              )
              .reduce((sum, other) => sum + other.amount, 0);
            const free =
              near && project.wished
                ? Math.max(0, availableBy(plan, project.wished) - earlier)
                : 0;
            const missing = near
              ? Math.max(0, project.amount - free)
              : (result?.missingAtWish ?? 0);
            const late = missing > 0;
            return (
              <Line
                key={project.id}
                title={project.name}
                detail={
                  <>
                    {`${money(project.amount)} · ${deadlineText(project)}`}
                    {late && (
                      <>
                        <br />
                        {t(
                          'Il manquera {{amount}}, ou {{hours}} de plus par semaine.',
                          {
                            amount: money(missing),
                            hours: hoursLabel(
                              extraHours(missing, project.wished),
                            ),
                          },
                        )}
                      </>
                    )}
                  </>
                }
                value={
                  near
                    ? late
                      ? t('Pas à temps')
                      : t('Finançable')
                    : result?.funded
                      ? monthName(result.funded)
                      : t('Hors de portée')
                }
                valueColor={late ? theme.warningText : theme.noticeTextLight}
              />
            );
          })}
          <View style={{ padding: 12 }}>
            <Button
              onPress={() => navigate('/previsions?ajouter=investissement')}
              style={{ minHeight: 44 }}
            >
              <Trans>Ajouter ou modifier dans Prévisions</Trans>
            </Button>
          </View>
        </Section>
      </View>
    </MtxPage>
  );
}
